import { useEffect, useState } from 'react';
import { msUntilStopBoundary } from './stop-state';

/**
 * FRONT-2341: forces exactly one extra re-render at the instant a pending
 * stop crosses `STOP_SLOW_THRESHOLD_MS`, so a card/panel already mounted on
 * "Stopping…" flips to "Stop again" + the slow note on its own. Without
 * this, nothing re-renders the caller at that moment: TanStack's structural
 * sharing means the 3s poll's response object is unchanged (same run, same
 * `stopRequestedAt`) and so never triggers a re-render, and there is no SSE
 * event for "time passed" — only for a real state change on the run itself.
 *
 * `observedAt` is the same first-sighting time the caller handed
 * `deriveStopState`, so the timer fires on the same clock the label reads.
 *
 * Thin effect wiring only — the arithmetic (`msUntilStopBoundary`) is
 * unit-tested in `stop-state.test.ts`. This repo's frontend unit harness
 * renders via `renderToStaticMarkup` and never runs `useEffect` (no jsdom),
 * so the scheduling itself is proven only in the browser: the `@verify`
 * specs E2E-CHAT-STOP-01 (chat card) and E2E-MACHINE-DESKTOP-08 (panel)
 * install a fake page clock, see "Stopping…", advance it past 30 s with the
 * surface still open, and see the note plus an enabled "Stop again".
 */
export function useStopBoundaryTimer(stopRequestedAt: string | null, observedAt: number | null): void {
  const [, forceRender] = useState(0);
  useEffect(() => {
    const remaining = msUntilStopBoundary(stopRequestedAt, observedAt, Date.now());
    if (remaining === null) return;
    const timer = setTimeout(() => forceRender((n) => n + 1), remaining);
    return () => clearTimeout(timer);
  }, [stopRequestedAt, observedAt]);
}
